import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import BreakdownSelector from "./BreakdownSelector"
import FreeLinesEditor from "./FreeLinesEditor"
import { fetchFormData, fetchCategoryFields, fetchOrderableBreakdowns, createPurchaseOrder } from "../api/purchase_orders"
import { computeTotals, formatMoney } from "../helpers/poTotals"
import QuillInput from "@shared/quillInput"

const LABEL    = "block text-xs font-medium text-slate-700 mb-1.5"
const INPUT    = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 transition"
const TEXTAREA = "block w-full px-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 transition"
const SELECT   = `${INPUT} bg-white`

const t = (key, opts) => I18n.t(`projects.engagements.form.${key}`, opts)
const Required = () => <span className="text-rose-500"> *</span>

const ANSWER_KEY = {
  string: "string_answer", options: "string_answer", link_url: "string_answer",
  date: "date_answer", integer: "integer_answer", float: "float_answer", boolean: "boolean_answer",
}

const PickerField = ({ value, placeholder, staticOptions, onSelect }) => {
  const [query, setQuery]     = useState("")
  const [open, setOpen]       = useState(false)
  const [results, setResults] = useState([])
  const wrapRef = useRef(null)

  useEffect(() => {
    const onDocClick = (event) => { if (!wrapRef.current?.contains(event.target)) { setOpen(false); setQuery("") } }
    document.addEventListener("click", onDocClick, true)
    return () => document.removeEventListener("click", onDocClick, true)
  }, [])

  const handleInput = (event) => {
    const next = event.target.value
    setQuery(next); setOpen(true)
    const needle = next.toLowerCase()
    setResults((staticOptions || []).filter((option) => (option.name || "").toLowerCase().includes(needle)).slice(0, 50))
  }
  const handleFocus = () => { setOpen(true); setQuery(""); setResults((staticOptions || []).slice(0, 50)) }
  const select = (option) => { onSelect(option); setQuery(""); setOpen(false) }

  return (
    <div className="relative" ref={wrapRef}>
      <input className={INPUT} value={open ? query : (value?.name || "")} onChange={handleInput} onFocus={handleFocus} placeholder={placeholder} />
      {open && (
        <div className="absolute z-20 mt-1 w-full min-w-[260px] bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">{t("no_match")}</div>
          ) : results.map((option) => (
            <button key={option.id} type="button" onClick={() => select(option)} className="w-full text-left px-3 py-2 text-sm text-slate-800 hover:bg-slate-50">
              {option.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const PropertyFieldInput = ({ field, value, onChange }) => {
  if (field.field_type === "sub_title") {
    return <div className="mt-1 text-[13px] font-semibold text-slate-800">{field.title}</div>
  }
  const set = (next) => onChange(field.property_field_blueprint_id, next)
  return (
    <div>
      {field.field_type !== "boolean" && <label className={LABEL}>{field.title}{field.required && <Required />}</label>}
      {field.field_type === "string"  && <textarea className={TEXTAREA} rows={2} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "date"    && <input type="date" className={INPUT} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "integer" && <input type="number" step="1" className={INPUT} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "float"   && <input type="number" step="0.01" className={INPUT} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "link_url" && <input type="url" className={INPUT} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "options" && (
        <select className={SELECT} value={value || ""} onChange={(event) => set(event.target.value)}>
          <option value="">—</option>
          {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      )}
      {field.field_type === "boolean" && (
        <label className="flex items-center gap-2 text-[12.5px] text-slate-700 mt-5">
          <input type="checkbox" className="h-4 w-4 rounded border border-slate-300" checked={!!value} onChange={(event) => set(event.target.checked)} />
          {field.title}{field.required && <Required />}
        </label>
      )}
    </div>
  )
}

const PurchaseOrderModal = ({ isOpen, projectId, lots = [], costCategories = [], productSearchUrl, onClose, onSaved }) => {
  const keyRef  = useRef(0)
  const nextKey = () => { keyRef.current += 1; return `f${keyRef.current}` }

  const [formData, setFormData] = useState(null)
  const [loading, setLoading]   = useState(false)
  const [saving, setSaving]     = useState(false)
  const [errors, setErrors]     = useState([])

  const [supplier, setSupplier] = useState(null)
  const [form, setForm]         = useState({})
  const [propertyFields, setPropertyFields] = useState([])
  const [propertyValues, setPropertyValues] = useState({})

  const [breakdowns, setBreakdowns]       = useState([])
  const [breakdownsLoading, setBreakdownsLoading] = useState(false)
  const [selection, setSelection]         = useState({})
  const [freeLines, setFreeLines]         = useState([])

  useEffect(() => {
    if (!isOpen) return
    setLoading(true); setErrors([]); setSupplier(null); setPropertyValues({}); setSelection({}); setFreeLines([])
    fetchFormData().then((data) => {
      setFormData(data)
      setForm({
        title: data.defaults?.title || "", description: "", category_id: "", manager_id: "", project_task_id: "",
        creation_date: data.defaults?.creation_date || "", expected_delivery_date: "",
        delivery_address: data.defaults?.delivery_address || "",
        destination_id: "", destination_type: "", currency: data.default_currency,
        observation: data.defaults?.observation || "",
      })
    }).finally(() => setLoading(false))
    fetchCategoryFields(null).then((fields) => setPropertyFields(Array.isArray(fields) ? fields : []))
    if (projectId) {
      setBreakdownsLoading(true)
      fetchOrderableBreakdowns(projectId)
        .then((rows) => setBreakdowns(Array.isArray(rows) ? rows : []))
        .finally(() => setBreakdownsLoading(false))
    }
  }, [isOpen, projectId])

  const vatRates       = formData?.vat_rates || []
  const vatRatesById   = useMemo(() => Object.fromEntries(vatRates.map((rate) => [rate.id, rate])), [vatRates])
  const currency       = form.currency || formData?.default_currency
  const breakdownsById = useMemo(() => Object.fromEntries(breakdowns.map((breakdown) => [breakdown.id, breakdown])), [breakdowns])

  const selectedLines = useMemo(() => (
    [...Object.values(selection), ...freeLines].map((entry) => ({
      quantity:    entry.quantity,
      unit_price:  entry.unit_price,
      vat_rate_id: supplier?.vat_rate_id || "",
    }))
  ), [selection, freeLines, supplier])

  const totals = useMemo(() => computeTotals(selectedLines, vatRatesById), [selectedLines, vatRatesById])

  const setField = (key, value) => setForm((previous) => ({ ...previous, [key]: value }))

  const onCategoryChanged = (categoryId) => {
    setField("category_id", categoryId)
    fetchCategoryFields(categoryId || null).then((fields) => setPropertyFields(Array.isArray(fields) ? fields : []))
  }

  const onStorehouseChanged = (storehouseId) => {
    const storehouse = (formData?.storehouses || []).find((entry) => String(entry.id) === String(storehouseId))
    setForm((previous) => ({
      ...previous,
      destination_id:   storehouse?.storehouse_id || "",
      destination_type: storehouse?.storehouse_type || "",
      delivery_address: storehouse?.address || previous.delivery_address,
    }))
  }

  const defaultEntry = (breakdown) => {
    // Quantity already ordered but budget left: pre-fill the complement to engage
    const budgetLeft = (breakdown.remaining_budget?.cents || 0) / 100
    if (breakdown.remaining_quantity <= 0 && budgetLeft > 0) return { quantity: 1, unit_price: budgetLeft }

    return {
      quantity:   breakdown.remaining_quantity,
      unit_price: (breakdown.unit_price?.cents || 0) / 100,
    }
  }

  const toggleBreakdown = (breakdown) => setSelection((previous) => {
    if (previous[breakdown.id]) {
      const { [breakdown.id]: _removed, ...rest } = previous
      return rest
    }
    return { ...previous, [breakdown.id]: defaultEntry(breakdown) }
  })

  const updateSelection = (id, patch) => setSelection((previous) => (
    previous[id] ? { ...previous, [id]: { ...previous[id], ...patch } } : previous
  ))

  const selectManyBreakdowns = (list, selected) => setSelection((previous) => {
    const next = { ...previous }
    list.forEach((breakdown) => {
      if (selected) next[breakdown.id] ||= defaultEntry(breakdown)
      else delete next[breakdown.id]
    })
    return next
  })

  const setPropertyValue = (blueprintId, value) => setPropertyValues((previous) => ({ ...previous, [blueprintId]: value }))

  const defaultCategory = costCategories.includes("materials") ? "materials" : (costCategories[0] || "materials")
  const addFreeLine    = (attrs) => setFreeLines((previous) => [...previous, { key: nextKey(), description: "", cost_category: defaultCategory, quantity: 1, unit: "", unit_price: "", product_id: null, ...attrs }])
  const updateFreeLine = (key, patch) => setFreeLines((previous) => previous.map((line) => (line.key === key ? { ...line, ...patch } : line)))
  const removeFreeLine = (key) => setFreeLines((previous) => previous.filter((line) => line.key !== key))

  const buildPayload = () => {
    const quotedLines = Object.entries(selection).map(([id, entry]) => {
      const breakdown = breakdownsById[id]
      return {
        description:        entry.description || breakdown?.label || breakdown?.qline_label || "",
        quantity:           entry.quantity,
        unit:               entry.unit || breakdown?.unit || "",
        unit_price:         entry.unit_price,
        vat_rate_id:        supplier?.vat_rate_id || null,
        qline_breakdown_id: Number(id),
        product_id:         entry.product_id ?? breakdown?.product_id ?? null,
      }
    })
    const extraLines = freeLines.map((line) => ({
      description:   line.description,
      quantity:      line.quantity,
      unit:          line.unit || "",
      unit_price:    line.unit_price,
      vat_rate_id:   supplier?.vat_rate_id || null,
      cost_category: line.cost_category,
      product_id:    line.product_id || null,
    }))
    const purchaseLines = [...quotedLines, ...extraLines].map((line, index) => ({ ...line, order: index + 1 }))

    const properties = propertyFields
      .filter((field) => field.field_type !== "sub_title")
      .map((field) => ({
        property_field_blueprint_id: field.property_field_blueprint_id,
        field_type: field.field_type, title: field.title, required: field.required,
        order: field.order, category_id: field.category_id, special_function: field.special_function,
        [ANSWER_KEY[field.field_type]]: propertyValues[field.property_field_blueprint_id] ?? "",
      }))

    const quoteIds = [...new Set(Object.keys(selection).map((id) => breakdownsById[id]?.quote_id).filter(Boolean))]

    return {
      quote_id: quoteIds.length === 1 ? quoteIds[0] : null,
      project_task_id: form.project_task_id || null,
      supplier_id: supplier?.id, title: form.title, description: form.description,
      category_id: form.category_id || null, manager_id: form.manager_id || null,
      creation_date: form.creation_date, expected_delivery_date: form.expected_delivery_date,
      delivery_address: form.delivery_address, destination_id: form.destination_id || null,
      destination_type: form.destination_type || null, currency, observation: form.observation,
      purchase_lines_attributes: purchaseLines, property_fields_attributes: properties,
    }
  }

  const handleSubmit = (commit) => {
    const clientErrors = []
    if (!supplier?.id) clientErrors.push(t("errors.supplier_required"))
    if (!form.title?.trim()) clientErrors.push(t("errors.title_required"))
    if (formData.mandatory_category && formData.categories?.length > 0 && !form.category_id) clientErrors.push(t("errors.category_required"))
    if (Object.keys(selection).length === 0 && freeLines.length === 0) clientErrors.push(t("errors.no_lines"))
    if (freeLines.some((line) => !line.description?.trim() || !(parseFloat(line.quantity) > 0) || line.unit_price === "" || isNaN(parseFloat(line.unit_price)))) {
      clientErrors.push(t("errors.free_line_incomplete"))
    }
    if (clientErrors.length) { setErrors(clientErrors); return }

    setSaving(true); setErrors([])
    createPurchaseOrder(projectId, buildPayload(), commit)
      .then((response) => {
        if (response?.errors) { setErrors(response.errors); setSaving(false); return }
        onSaved(response); onClose()
      })
      .catch(() => { setErrors([t("errors.network")]); setSaving(false) })
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="md" onClick={onClose} disabled={saving}>{t("cancel")}</Button>
        <Button variant="secondary" size="md" onClick={() => handleSubmit("Save")} disabled={saving || loading}>
          {saving && <FontAwesomeIcon icon="circle-notch" spin className="mr-1.5" />}{t("save")}
        </Button>
        <Button variant="primary" size="md" onClick={() => handleSubmit("Generate")} disabled={saving || loading}>
          {saving && <FontAwesomeIcon icon="circle-notch" spin className="mr-1.5" />}{t("generate")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} eyebrow={t("eyebrow")} title={t("title")} size="xl" footer={footer}>
      {loading || !formData ? (
        <div className="py-16 text-center text-slate-400"><FontAwesomeIcon icon="circle-notch" spin className="text-2xl" /></div>
      ) : (
        <div className="space-y-6">
          <section className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <label className={LABEL}>{t("supplier")}<Required /></label>
              <PickerField value={supplier} placeholder={t("search_supplier")} staticOptions={formData.suppliers} onSelect={setSupplier} />
            </div>
            <div className="col-span-12 md:col-span-6">
              <label className={LABEL}>{t("po_title")}<Required /></label>
              <input className={INPUT} value={form.title} onChange={(event) => setField("title", event.target.value)} />
            </div>
            <div className="col-span-12">
              <label className={LABEL}>{t("description")}</label>
              <textarea className={TEXTAREA} rows={2} value={form.description} onChange={(event) => setField("description", event.target.value)} />
            </div>
            {lots.length > 0 && (
              <div className="col-span-12 md:col-span-6">
                <label className={LABEL}>{t("lot")}</label>
                <select className={SELECT} value={form.project_task_id} onChange={(event) => setField("project_task_id", event.target.value)}>
                  <option value="">{t("lot_whole_project")}</option>
                  {lots.map((lot) => <option key={lot.id} value={lot.id}>{lot.title}</option>)}
                </select>
              </div>
            )}
            <div className={`col-span-12 ${lots.length > 0 ? "md:col-span-6" : "md:col-span-4"}`}>
              <label className={LABEL}>{t("manager")}</label>
              <select className={SELECT} value={form.manager_id} onChange={(event) => setField("manager_id", event.target.value)}>
                <option value="">—</option>
                {formData.managers.map((manager) => <option key={manager.id} value={manager.id}>{manager.name}</option>)}
              </select>
            </div>
            <div className="col-span-6 md:col-span-4">
              <label className={LABEL}>{t("creation_date")}</label>
              <input type="date" className={INPUT} value={form.creation_date} onChange={(event) => setField("creation_date", event.target.value)} />
            </div>
            <div className="col-span-6 md:col-span-4">
              <label className={LABEL}>{t("expected_delivery_date")}</label>
              <input type="date" className={INPUT} value={form.expected_delivery_date} onChange={(event) => setField("expected_delivery_date", event.target.value)} />
            </div>
            {formData.storehouse_module_active && (
              <div className="col-span-12 md:col-span-6">
                <label className={LABEL}>{t("destination")}</label>
                <select className={SELECT} onChange={(event) => onStorehouseChanged(event.target.value)} defaultValue="">
                  <option value="">—</option>
                  {formData.storehouses.map((storehouse) => <option key={storehouse.id} value={storehouse.id}>{storehouse.name}</option>)}
                </select>
              </div>
            )}
            <div className={`col-span-12 ${formData.storehouse_module_active ? "md:col-span-6" : "md:col-span-8"}`}>
              <label className={LABEL}>{t("delivery_address")}</label>
              <input className={INPUT} value={form.delivery_address} onChange={(event) => setField("delivery_address", event.target.value)} />
            </div>
            {formData.multi_currency && (
              <div className="col-span-12 md:col-span-4">
                <label className={LABEL}>{t("currency")}</label>
                <select className={SELECT} value={form.currency} onChange={(event) => setField("currency", event.target.value)}>
                  {formData.currencies.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
                </select>
              </div>
            )}
          </section>

          <BreakdownSelector
            breakdowns       = {breakdowns}
            selection        = {selection}
            currency         = {currency}
            loading          = {breakdownsLoading}
            onToggle         = {toggleBreakdown}
            onUpdate         = {updateSelection}
            onSelectMany     = {selectManyBreakdowns}
            productSearchUrl = {productSearchUrl}
          />

          <FreeLinesEditor
            lines            = {freeLines}
            currency         = {currency}
            costCategories   = {costCategories}
            productSearchUrl = {productSearchUrl}
            onAdd            = {addFreeLine}
            onUpdate         = {updateFreeLine}
            onRemove         = {removeFreeLine}
          />

          {formData.categories_enabled && (formData.categories?.length > 0 || propertyFields.length > 0) && (
            <section>
              <div className="space-y-4">
                {formData.categories?.length > 0 && (
                  <div>
                    <label className={LABEL}>{t("category")}{formData.mandatory_category && <Required />}</label>
                    <select className={SELECT} value={form.category_id} onChange={(event) => onCategoryChanged(event.target.value)}>
                      <option value="">—</option>
                      {formData.categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                    </select>
                  </div>
                )}
                {propertyFields.map((field) => (
                  <PropertyFieldInput key={field.property_field_blueprint_id} field={field} value={propertyValues[field.property_field_blueprint_id]} onChange={setPropertyValue} />
                ))}
              </div>
            </section>
          )}

          <div className="grid grid-cols-12 gap-6">
            <section className="col-span-12 md:col-span-7">
              <label className={LABEL}>{t("observation")}</label>
              <QuillInput text={form.observation || ""} onChange={(content) => setField("observation", content)} />
            </section>
            <section className="col-span-12 md:col-span-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-1.5">
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-slate-500">{t("subtotal_ht")}</span>
                  <span className="tabular-nums font-medium text-slate-900">{formatMoney(totals.subtotal, currency)}</span>
                </div>
                {totals.vatBreakdown.map((vat) => (
                  <div key={vat.percentage} className="flex items-center justify-between text-[12.5px]">
                    <span className="text-slate-400">{t("vat")} {Math.round(vat.percentage)}%</span>
                    <span className="tabular-nums text-slate-600">{formatMoney(vat.amount, currency)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-[15px] pt-1.5 border-t border-slate-200">
                  <span className="font-semibold text-slate-700">{t("total_ttc")}</span>
                  <span className="tabular-nums font-semibold text-slate-900">{formatMoney(totals.total, currency)}</span>
                </div>
              </div>
            </section>
          </div>

          {errors.length > 0 && (
            <div className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">{errors.join(", ")}</div>
          )}
        </div>
      )}
    </Modal>
  )
}

export default PurchaseOrderModal
